/** One non-trickle offer: only send the browser's final SDP after ICE gathering.
 * Never log SDP, credentials, addresses, device IDs, transcripts or audio.
 */
export async function gatheredVoiceOffer(pc:RTCPeerConnection,signal:AbortSignal,timeoutMs=10000):Promise<string>{
 if(signal.aborted)throw new Error('voice_start_cancelled');
 if(pc.iceGatheringState!=='complete')await new Promise<void>((resolve,reject)=>{
  let timer:ReturnType<typeof setTimeout>;
  const cleanup=()=>{clearTimeout(timer);pc.removeEventListener('icegatheringstatechange',onState);signal.removeEventListener('abort',onAbort);};
  const onAbort=()=>{cleanup();reject(new Error('voice_start_cancelled'));};
  const onState=()=>{if(pc.iceGatheringState==='complete'){cleanup();resolve();}};
  timer=setTimeout(()=>{cleanup();reject(new Error('La rete non ha completato il collegamento audio. Riprova o continua a scrivere.'));},timeoutMs);
  pc.addEventListener('icegatheringstatechange',onState);signal.addEventListener('abort',onAbort,{once:true});
  if(signal.aborted)onAbort();else onState();
 });
 if(signal.aborted)throw new Error('voice_start_cancelled');
 const sdp=pc.localDescription?.sdp;
 if(typeof sdp!=='string'||!sdp.startsWith('v=0')||sdp.length>64000)throw new Error('Non riesco a preparare il collegamento audio. Puoi riprovare.');
 return sdp;
}
const states={
 connection:['new','connecting','connected','disconnected','failed','closed'],
 ice:['new','checking','connected','completed','disconnected','failed','closed'],
 gathering:['new','gathering','complete'],
 signaling:['stable','have-local-offer','have-remote-offer','have-local-pranswer','have-remote-pranswer','closed']
};
export type VoiceTransportStage='local_offer'|'gathered_offer'|'remote_answer'|'connection'|'ice'|'events_open'|'stop';
export function voiceTransportSnapshot(pc:Pick<RTCPeerConnection,'connectionState'|'iceConnectionState'|'iceGatheringState'|'signalingState'|'localDescription'|'remoteDescription'>){
 const state=(value:unknown,allowed:string[])=>typeof value==='string'&&allowed.includes(value)?value:'unknown';
 const count=(description:RTCSessionDescription|null)=>Math.min(64,(description?.sdp?.match(/^a=candidate:/gm)||[]).length);
 return {connection:state(pc.connectionState,states.connection),ice:state(pc.iceConnectionState,states.ice),gathering:state(pc.iceGatheringState,states.gathering),signaling:state(pc.signalingState,states.signaling),local_candidates:count(pc.localDescription),remote_candidates:count(pc.remoteDescription)};
}
export function voiceTransportCounters(report:RTCStatsReport){
 const number=(value:unknown)=>typeof value==='number'&&Number.isFinite(value)&&value>=0&&value<1e12?Math.round(value):0;
 const result={pairs_succeeded:0,selected_pair:false,rtt_ms:0,audio_packets_in:0,audio_packets_out:0,audio_bytes_in:0,audio_bytes_out:0,audio_packets_lost:0};
 report.forEach((s:any)=>{
  if(s.type==='candidate-pair'&&s.state==='succeeded'){result.pairs_succeeded++;if(s.nominated===true){result.selected_pair=true;result.rtt_ms=number(typeof s.currentRoundTripTime==='number'?s.currentRoundTripTime*1000:0);}}
  if((s.kind||s.mediaType)!=='audio')return;
  if(s.type==='inbound-rtp'){result.audio_packets_in+=number(s.packetsReceived);result.audio_bytes_in+=number(s.bytesReceived);result.audio_packets_lost+=number(s.packetsLost);}
  if(s.type==='outbound-rtp'){result.audio_packets_out+=number(s.packetsSent);result.audio_bytes_out+=number(s.bytesSent);}
 });
 return result;
}
export function logVoiceTransport(pc:RTCPeerConnection,stage:VoiceTransportStage){
 console.info('[FluTransport]',JSON.stringify({stage,...voiceTransportSnapshot(pc)}));
 if(stage==='events_open'||stage==='stop'||pc.connectionState==='failed'||pc.connectionState==='disconnected'){
  void pc.getStats().then(report=>console.info('[FluTransportStats]',JSON.stringify({stage,...voiceTransportCounters(report)}))).catch(()=>{});
 }
}
